import { z } from 'zod'

import {
  MANAGEMENT_USER_MAX_FAVORITE_VENUES,
  ManagementUserLanguagePreference,
  ManagementUserRole,
  ManagementUserSignUpType,
  ManagementUserThemePreference,
} from '../constants/managementUser'
import { PushClientType } from '../constants/push'
import { DateDisplayFormat } from '../constants/venue'
import { listQueryZodFields } from './system'
import { dbEntitySchema } from './system'

export const managementUserSchema = dbEntitySchema.extend({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  name: z.string().nullish(),
  email: z.string(),
  google: z
    .object({
      id: z.string().nullish(),
      email: z.string().email().nullish(),
      avatar: z.string().nullish(),
    })
    .nullish(),
  phone: z.string().nullish(),
  role: z.nativeEnum(ManagementUserRole),
  profileImage: z.string().nullish(),
  languagePreference: z
    .nativeEnum(ManagementUserLanguagePreference)
    .optional()
    .nullable(),
  dateDisplayPreference: z.nativeEnum(DateDisplayFormat).optional().nullable(),
  themePreference: z
    .nativeEnum(ManagementUserThemePreference)
    .optional()
    .nullable(),
  utm_source: z.string().nullish(),
  utm_medium: z.string().nullish(),
  utm_campaign: z.string().nullish(),
  utm_term: z.string().nullish(),
  utm_content: z.string().nullish(),
  signUpType: z.nativeEnum(ManagementUserSignUpType).optional(),
  organizations: z.array(z.string()).nullish(),
  favoriteVenues: z.array(z.string()).nullish(),
  acceptedTerms: z.boolean().optional(),
  // Deprecated compatibility fields retained for API/service/report compatibility:
  marketingEmailOptIn: z.boolean().nullish(),
  marketingEmailOptInAt: z.date().nullish(),
  marketingSmsOptIn: z.boolean().nullish(),
  marketingSmsOptInAt: z.date().nullish(),
  lastActivityAt: z.coerce.date().nullish(),
  emailVerificationRequired: z.boolean().optional(),
  emailVerifiedAt: z.date().nullish(),
})

export const managementUsersListQuerySchema = z.object({
  ...listQueryZodFields,
  id: z.array(z.string()).or(z.string()).optional(),
  search: z.string().nullish(),
  role: z.nativeEnum(ManagementUserRole).nullish(),
})

export const managementUsersListResponseSchema = z.object({
  data: z.array(managementUserSchema),
  count: z.number(),
})

export const managementUserChangePasswordBodySchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(1),
})

export const managementUserInviteAdminBodySchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().nullish(),
  profileImage: z.string().nullish(),
  google: z
    .object({
      id: z.string().nullish(),
      email: z.string().email().nullish(),
      avatar: z.string().nullish(),
    })
    .optional(),
  acceptedTerms: z.boolean().optional(),
  emailVerificationRequired: z.boolean().optional(),
  role: z.nativeEnum(ManagementUserRole).optional(),
})

export const managementUserAdminPushDeviceRowSchema = z.object({
  id: z.string(),
  token: z.string(),
  type: z.nativeEnum(PushClientType),
  device: z.string().nullish(),
  created: z.coerce.date(),
})

export const managementUserAdminPushDevicesResponseSchema = z.object({
  data: z.array(managementUserAdminPushDeviceRowSchema),
})

export const managementUserAdminSendPushBodySchema = z.object({
  message: z.string().trim().min(1),
  title: z.string().trim().min(1).optional(),
  tokens: z.array(z.string().trim().min(1)).min(1).optional(),
})

export const managementUserAdminSendPushResponseSchema = z.object({
  data: z.object({
    sentCount: z.number(),
  }),
})

/** Fields accepted when creating a management user (service layer). */
export const managementUserCreateDataSchema = z.object({
  role: z.nativeEnum(ManagementUserRole).optional(),
  email: z.string().email(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  phone: z.string().nullish(),
  profileImage: z.string().nullish(),
  languagePreference: z.nativeEnum(ManagementUserLanguagePreference).optional(),
  dateDisplayPreference: z.nativeEnum(DateDisplayFormat).optional(),
  acceptedTerms: z.boolean().optional(),
  utm_source: z.string().nullish(),
  utm_medium: z.string().nullish(),
  utm_campaign: z.string().nullish(),
  utm_term: z.string().nullish(),
  utm_content: z.string().nullish(),
  signUpType: z.nativeEnum(ManagementUserSignUpType).optional(),
  favoriteVenues: z
    .array(z.string())
    .max(MANAGEMENT_USER_MAX_FAVORITE_VENUES)
    .optional(),
  // Deprecated compatibility fields retained for service update/create flows:
  marketingEmailOptIn: z.boolean().optional(),
  marketingSmsOptIn: z.boolean().optional(),
  emailVerificationRequired: z.boolean().optional(),
  emailVerifiedAt: z.date().nullish(),
})

export const managementUserUpdateDataSchema = z.object({
  email: z.string().email().optional(),
  name: z.string().nullish().optional(),
  firstName: z.string().nullish().optional(),
  lastName: z.string().nullish().optional(),
  phone: z.string().nullish(),
  profileImage: z.string().nullish(),
  languagePreference: z
    .nativeEnum(ManagementUserLanguagePreference)
    .optional()
    .nullable(),
  dateDisplayPreference: z.nativeEnum(DateDisplayFormat).optional().nullable(),
  themePreference: z
    .nativeEnum(ManagementUserThemePreference)
    .optional()
    .nullable(),
  acceptedTerms: z.boolean().optional(),
  favoriteVenues: z
    .array(z.string())
    .max(MANAGEMENT_USER_MAX_FAVORITE_VENUES)
    .optional(),
  // Deprecated compatibility fields retained for service update flows:
  marketingEmailOptIn: z.boolean().optional(),
  marketingEmailOptInAt: z.date().optional(),
  marketingSmsOptIn: z.boolean().optional(),
  marketingSmsOptInAt: z.date().optional(),
  emailVerificationRequired: z.boolean().optional(),
  emailVerifiedAt: z.date().nullish().optional(),
})

export const inviteManagementUserArgsSchema =
  managementUserCreateDataSchema.extend({
    organizationId: z.string(),
  })

export const inviteAdminArgsSchema = managementUserCreateDataSchema.extend({
  role: z.nativeEnum(ManagementUserRole).optional(),
})

export const registerManagementUserArgsSchema = managementUserCreateDataSchema
